import type { Folder, FileSystem, Prompt } from '../types';

export const findPromptById = (fs: FileSystem, promptId: string): Prompt | null => {
    if (!fs || !Array.isArray(fs)) return null;
    for (const folder of fs) {
        if ((folder.prompts || [])) {
            const prompt = (folder.prompts || []).find(p => p.id === promptId);
            if (prompt) return prompt;
        }
        if ((folder.subFolders || []) && (folder.subFolders || []).length > 0) {
            const found = findPromptById((folder.subFolders || []), promptId);
            if (found) return found;
        }
    }
    return null;
};

export const findFolder = (fs: FileSystem, folderId: string): Folder | null => {
    if (!fs || !Array.isArray(fs)) return null;
    for (const folder of fs) {
        if (folder.id === folderId) return folder;
        if ((folder.subFolders || []) && (folder.subFolders || []).length > 0) {
            const found = findFolder((folder.subFolders || []), folderId);
            if (found) return found;
        }
    }
    return null;
};

export const findPromptAndParentFolder = (fs: FileSystem, promptId: string): { prompt: Prompt, parent: Folder } | null => {
    if (!fs || !Array.isArray(fs)) return null;
    for (const folder of fs) {
        if ((folder.prompts || [])) {
            const prompt = (folder.prompts || []).find(p => p.id === promptId);
            if (prompt) {
                return { prompt, parent: folder };
            }
        }
        if ((folder.subFolders || []) && (folder.subFolders || []).length > 0) {
            const found = findPromptAndParentFolder((folder.subFolders || []), promptId);
            if (found) return found;
        }
    }
    return null;
};

export const getAllPrompts = (fs: FileSystem): Prompt[] => {
    if (!fs || !Array.isArray(fs)) return [];
    const prompts: Prompt[] = [];
    const traverse = (folders: Folder[]) => {
        if (!folders || !Array.isArray(folders)) return;
        for (const folder of folders) {
            if ((folder.prompts || [])) {
                prompts.push(...(folder.prompts || []));
            }
            if ((folder.subFolders || []) && (folder.subFolders || []).length > 0) {
                traverse((folder.subFolders || []));
            }
        }
    };
    traverse(fs);
    return prompts;
};

export const getAllFolders = (fs: FileSystem): Folder[] => {
    if (!fs || !Array.isArray(fs)) return [];
    const folders: Folder[] = [];
    const traverse = (items: Folder[]) => {
        if (!items || !Array.isArray(items)) return;
        for (const folder of items) {
            folders.push(folder);
            if ((folder.subFolders || []) && (folder.subFolders || []).length > 0) {
                traverse((folder.subFolders || []));
            }
        }
    };
    traverse(fs);
    return folders;
};

export const updatePromptInFS = (fs: FileSystem, updatedPrompt: Prompt): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.map(folder => {
        const promptIndex = (folder.prompts || []).findIndex(p => p.id === updatedPrompt.id);
        if (promptIndex !== -1) {
            const newPrompts = [...(folder.prompts || [])];
            const existingPrompt = newPrompts[promptIndex];
            newPrompts[promptIndex] = {
                ...updatedPrompt,
                deletedAt: existingPrompt.deletedAt,
                originalFolderId: existingPrompt.originalFolderId
            };
            return { ...folder, prompts: newPrompts };
        }
        if ((folder.subFolders || [])) {
            return { ...folder, subFolders: updatePromptInFS((folder.subFolders || []), updatedPrompt) };
        }
        return folder;
    });
};

export const addPromptToFolderFS = (fs: FileSystem, folderId: string, prompt: Prompt, index?: number): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.map(folder => {
        if (folder.id === folderId) {
            const newPrompts = [...(folder.prompts || [])];
            if (index === undefined || index < 0 || index > newPrompts.length) {
                newPrompts.unshift(prompt);
            } else {
                newPrompts.splice(index, 0, prompt);
            }
            return { ...folder, prompts: newPrompts };
        }
        if ((folder.subFolders || [])) {
            return { ...folder, subFolders: addPromptToFolderFS((folder.subFolders || []), folderId, prompt, index) };
        }
        return folder;
    });
};

export const addPromptsToFolderFS = (fs: FileSystem, folderId: string, prompts: Prompt[], index?: number): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.map(folder => {
        if (folder.id === folderId) {
            const newPrompts = [...(folder.prompts || [])];
            const clampedIndex = index === undefined || index < 0 || index > newPrompts.length ? 0 : index;
            newPrompts.splice(clampedIndex, 0, ...prompts);
            return { ...folder, prompts: newPrompts };
        }
        if ((folder.subFolders || [])) {
            return { ...folder, subFolders: addPromptsToFolderFS((folder.subFolders || []), folderId, prompts, index) };
        }
        return folder;
    });
};

export const addFolderToFS = (fs: FileSystem, parentId: string | null, newFolder: Folder, index?: number): FileSystem => {
    if (!fs || !Array.isArray(fs)) {
        return parentId === null ? [newFolder] : [];
    }
    if (parentId === null) {
        const newFs = [...fs];
        if (index === undefined || index < 0 || index > newFs.length) {
            newFs.push(newFolder);
        } else {
            newFs.splice(index, 0, newFolder);
        }
        return newFs;
    }
    return fs.map(folder => {
        if (folder.id === parentId) {
            const newSubFolders = [...(folder.subFolders || [])];
            if (index === undefined || index < 0 || index > newSubFolders.length) {
                newSubFolders.push(newFolder);
            } else {
                newSubFolders.splice(index, 0, newFolder);
            }
            return { ...folder, subFolders: newSubFolders };
        }
        if ((folder.subFolders || [])) {
            return { ...folder, subFolders: addFolderToFS((folder.subFolders || []), parentId, newFolder, index) };
        }
        return folder;
    });
};

export const deleteFolderFromFS = (fs: FileSystem, folderId: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    const folderToDelete = findFolder(fs, folderId);
    if (!folderToDelete) return fs;

    // 1. Collect all prompts in the folder tree and mark them as deleted
    const allPromptsInTree: Prompt[] = [];
    const traverse = (f: Folder) => {
        allPromptsInTree.push(...f.prompts.map(p => ({
            ...p,
            deletedAt: p.deletedAt || new Date().toISOString(),
            originalFolderId: p.originalFolderId || f.id
        })));
        if (f.subFolders) {
            f.subFolders.forEach(traverse);
        }
    };
    traverse(folderToDelete);

    // 2. Remove the folder from FS
    let nextFs = extractFolderFromFS(fs, folderId);

    // 3. Add collected prompts to trash
    if (allPromptsInTree.length > 0) {
        const trashId = 'trash';
        const trashIndex = nextFs.findIndex(f => f.id === trashId);

        if (trashIndex >= 0) {
            const trashFolder = nextFs[trashIndex];
            const updatedTrash: Folder = {
                ...trashFolder,
                prompts: [...allPromptsInTree, ...trashFolder.prompts],
            };
            nextFs = nextFs.map((f, i) => (i === trashIndex ? updatedTrash : f));
        } else {
            const trashFolder: Folder = {
                id: trashId,
                name: 'Papierkorb',
                prompts: allPromptsInTree,
                subFolders: [],
            };
            nextFs = [...nextFs, trashFolder];
        }
    }

    return nextFs;
};

export const renameFolderInFS = (fs: FileSystem, folderId: string, newName: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.map(folder => {
        if (folder.id === folderId) {
            return { ...folder, name: newName };
        }
        if ((folder.subFolders || [])) {
            return { ...folder, subFolders: renameFolderInFS((folder.subFolders || []), folderId, newName) };
        }
        return folder;
    });
};

export const setFolderColorInFS = (fs: FileSystem, folderId: string, color: string | undefined): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.map(folder => {
        if (folder.id === folderId) {
            return { ...folder, color };
        }
        if ((folder.subFolders || [])) {
            return { ...folder, subFolders: setFolderColorInFS((folder.subFolders || []), folderId, color) };
        }
        return folder;
    });
};

export const removePromptFromFS = (fs: FileSystem, promptId: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.map(folder => {
        const newPrompts = (folder.prompts || []).filter(p => p.id !== promptId);
        const subFolders = (folder.subFolders || []) ? removePromptFromFS((folder.subFolders || []), promptId) : [];
        return { ...folder, prompts: newPrompts, subFolders };
    });
};

export const removePromptsFromFS = (fs: FileSystem, promptIds: string[]): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    const idSet = new Set(promptIds);
    const traverse = (folders: Folder[]): Folder[] => {
        if (!folders || !Array.isArray(folders)) return [];
        return folders.map(folder => {
            const newPrompts = (folder.prompts || []).filter(p => !idSet.has(p.id));
            const subFolders = (folder.subFolders || []) ? traverse((folder.subFolders || [])) : [];
            return { ...folder, prompts: newPrompts, subFolders };
        });
    };
    return traverse(fs);
};

export const deletePromptFromFS = (fs: FileSystem, promptId: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    const info = findPromptAndParentFolder(fs, promptId);
    if (!info) return fs;

    const { prompt, parent } = info;
    if (parent.id === 'trash') return fs;

    const promptWithDeletion: Prompt = {
        ...prompt,
        deletedAt: new Date().toISOString(),
        originalFolderId: parent.id,
    };

    // remove the prompt from its original location
    let fsWithout = removePromptFromFS(fs, promptId);

    // ensure a trash folder exists and prepend the deleted prompt
    const trashId = 'trash';
    const trashIndex = fsWithout.findIndex(f => f.id === trashId);

    if (trashIndex >= 0) {
        const trashFolder = fsWithout[trashIndex];
        const updatedTrash: Folder = {
            ...trashFolder,
            prompts: [promptWithDeletion, ...(trashFolder.prompts || [])],
        };
        fsWithout = fsWithout.map((f, i) => (i === trashIndex ? updatedTrash : f));
    } else {
        const trashFolder: Folder = {
            id: trashId,
            name: 'Papierkorb',
            prompts: [promptWithDeletion],
            subFolders: [],
        };
        fsWithout = [...fsWithout, trashFolder];
    }

    return fsWithout;
};

export const restorePromptFromTrash = (fs: FileSystem, promptId: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    const info = findPromptAndParentFolder(fs, promptId);
    if (!info || info.parent.id !== 'trash') return fs;
    
    const { prompt } = info;
    const restoredPrompt: Prompt = { ...prompt, deletedAt: null };
    
    let fsWithout = removePromptFromFS(fs, promptId);
    const targetId = prompt.originalFolderId;
    
    // Check if target folder still exists and is not trash
    const targetFolder = (targetId && targetId !== 'trash') ? findFolder(fsWithout, targetId) : null;
    
    if (targetFolder) {
        fsWithout = addPromptToFolderFS(fsWithout, targetFolder.id, restoredPrompt);
    } else {
        // Fallback to first non-trash folder if original is missing
        const fallbackFolder = fsWithout.find(f => f.id !== 'trash');
        if (fallbackFolder) {
            fsWithout = addPromptToFolderFS(fsWithout, fallbackFolder.id, restoredPrompt);
        } else {
            // Create a default folder if completely empty or only trash exists
            const defaultFolder: Folder = { id: 'default', name: 'Wiederhergestellt', prompts: [restoredPrompt], subFolders: [] };
            fsWithout = [...fsWithout, defaultFolder];
        }
    }
    return fsWithout;
};

export const permanentlyDeletePrompt = (fs: FileSystem, promptId: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return removePromptFromFS(fs, promptId);
};

export const cleanupTrash = (fs: FileSystem, days: number = 30, removeEmptyTrashFolder: boolean = false): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
    const cleaned = fs.map(folder => {
        if (folder.id === 'trash') {
            const newPrompts = (folder.prompts || []).filter(p => !p.deletedAt || new Date(p.deletedAt).getTime() > threshold);
            return { ...folder, prompts: newPrompts };
        }
        return folder;
    });

    if (!removeEmptyTrashFolder) {
        return cleaned;
    }

    return cleaned.filter(folder => folder.id !== 'trash' || (folder.prompts || []).length > 0);
};

/** Check if `potentialDescendantId` is nested anywhere inside `folderId` */
export const isDescendantOf = (fs: FileSystem, folderId: string, potentialDescendantId: string): boolean => {
    if (!fs || !Array.isArray(fs)) return false;
    const folder = findFolder(fs, folderId);
    if (!folder) return false;
    const checkDescendant = (f: Folder): boolean => {
        for (const sub of (f.subFolders || [])) {
            if (sub.id === potentialDescendantId) return true;
            if (checkDescendant(sub)) return true;
        }
        return false;
    };
    return checkDescendant(folder);
};

/** Remove a folder from the tree WITHOUT trashing its prompts.
 *  Used by moveFolderInFS to extract a folder before re-inserting it elsewhere. */
export const extractFolderFromFS = (fs: FileSystem, folderId: string): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    const filtered = fs.filter(f => f.id !== folderId);
    if (filtered.length !== fs.length) {
        return filtered;
    }
    return fs.map(folder => {
        if ((folder.subFolders || []) && (folder.subFolders || []).length > 0) {
            return { ...folder, subFolders: extractFolderFromFS((folder.subFolders || []), folderId) };
        }
        return folder;
    });
};

/** Move a folder to a new parent (or root when targetParentId is null).
 *  Returns unchanged fs if the move is invalid (e.g. circular). */
export const moveFolderInFS = (fs: FileSystem, folderId: string, targetParentId: string | null, index?: number): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    // Don't move to self
    if (folderId === targetParentId) return fs;
    // Don't move trash
    if (folderId === 'trash') return fs;
    // Don't move into own descendant (circular)
    if (targetParentId && isDescendantOf(fs, folderId, targetParentId)) return fs;

    // Target folder check before extraction!
    if (targetParentId !== null && !findFolder(fs, targetParentId)) return fs;

    // Find the folder (with its full subtree and prompts intact)
    const folderToMove = findFolder(fs, folderId);
    if (!folderToMove) return fs;

    // Deep-clone the folder so we preserve its prompts and subfolders
    const clonedFolder: Folder = structuredClone(folderToMove);

    // Extract (not delete!) from current location — prompts stay untouched
    let fsWithout = extractFolderFromFS(fs, folderId);

    // Add to new location with index support
    fsWithout = addFolderToFS(fsWithout, targetParentId, clonedFolder, index);

    return fsWithout;
};

export const emptyTrash = (fs: FileSystem): FileSystem => {
    if (!fs || !Array.isArray(fs)) return [];
    return fs.filter(folder => folder.id !== 'trash');
};

/** Recursively counts all prompts and subfolders inside a folder.
 * Returns { promptCount, folderCount } */
export const getFolderContentMetrics = (folder: Folder | null | undefined): { promptCount: number; folderCount: number } => {
    if (!folder) return { promptCount: 0, folderCount: 0 };
    let promptCount = (folder.prompts || []).length;
    let folderCount = (folder.subFolders || []).length;

    for (const sub of (folder.subFolders || [])) {
        const subMetrics = getFolderContentMetrics(sub);
        promptCount += subMetrics.promptCount;
        folderCount += subMetrics.folderCount;
    }

    return { promptCount, folderCount };
};

export const getFolderPath = (fs: FileSystem, folderId: string): Folder[] => {
    const path: Folder[] = [];
    const traverse = (folders: Folder[], targetId: string): boolean => {
        if (!folders || !Array.isArray(folders)) return false;
        for (const folder of folders) {
            path.push(folder);
            if (folder.id === targetId) return true;
            if (folder.subFolders && traverse(folder.subFolders, targetId)) return true;
            path.pop();
        }
        return false;
    };
    traverse(fs, folderId);
    return path;
};
